import { ExclamationTriangleIcon, UserIcon } from '@heroicons/react/24/outline'
import { formatThreatLevel } from '../../utils/amlResultCategories'
import { isWatchlistAmlProvider, sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding'

/**
 * Subject-level watchlist screening context (who was screened, profile count, PEP exposure).
 */
export default function GreenIdScreeningSummary({ summary, compact = false }) {
  if (!summary || !isWatchlistAmlProvider(summary.provider)) return null

  const subject = summary.subject || {}
  const counts = summary.exposureCounts || {}
  const pepLists = summary.pepListHits || []
  const narrative = summary.narrative ? sanitizeVendorBranding(summary.narrative) : null

  return (
    <div
      className={`border rounded-lg ${
        summary.needsReview
          ? 'border-amber-300 bg-amber-50'
          : 'border-gray-200 bg-gray-50'
      } ${compact ? 'p-3 mb-3' : 'p-4 mb-4'}`}
    >
      <div className="flex items-start gap-3">
        {summary.needsReview && (
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
            Screened subject
          </h4>
          <p className={`text-gray-900 font-medium ${compact ? 'text-sm' : 'text-lg'}`}>
            <UserIcon className="inline h-4 w-4 mr-1 text-gray-500" />
            {subject.fullName || '—'}
            {subject.country && (
              <span className="text-gray-600 font-normal text-sm ml-2">({subject.country})</span>
            )}
          </p>

          <div className={`mt-3 flex flex-wrap gap-2 text-sm ${compact ? '' : ''}`}>
            <span
              className={`px-2.5 py-1 rounded-md border font-medium ${
                summary.pepStatus === 'yes'
                  ? 'bg-red-100 border-red-300 text-red-900'
                  : 'bg-gray-100 border-gray-300 text-gray-700'
              }`}
            >
              PEP: {summary.pepStatus === 'yes' ? 'Yes' : 'No'}
            </span>
            <span
              className={`px-2.5 py-1 rounded-md border font-medium ${
                summary.adverseMediaStatus === 'yes'
                  ? 'bg-amber-100 border-amber-300 text-amber-900'
                  : 'bg-gray-100 border-gray-300 text-gray-700'
              }`}
            >
              Adverse media: {summary.adverseMediaStatus === 'yes' ? 'Yes' : 'No'}
            </span>
            <span
              className={`px-2.5 py-1 rounded-md border font-medium ${
                summary.sanctionsStatus === 'yes'
                  ? 'bg-purple-100 border-purple-300 text-purple-900'
                  : 'bg-gray-100 border-gray-300 text-gray-700'
              }`}
            >
              Sanctions: {summary.sanctionsStatus === 'yes' ? 'Yes' : 'No'}
            </span>
          </div>

          <dl className={`mt-2 grid gap-x-4 gap-y-1 text-sm ${compact ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {summary.verificationId && (
              <>
                <dt className="text-gray-600">Screening reference</dt>
                <dd className="font-mono text-gray-900">{summary.verificationId}</dd>
              </>
            )}
            {summary.overallStatus && (
              <>
                <dt className="text-gray-600">Overall status</dt>
                <dd className="font-medium text-gray-900">
                  {formatThreatLevel(summary.overallStatus)}
                </dd>
              </>
            )}
            {summary.profilesMatchedCount != null && (
              <>
                <dt className="text-gray-600">Structured profiles</dt>
                <dd className="font-medium text-gray-900">
                  {summary.profilesMatchedCount} matched
                  <span className="block text-xs font-normal text-gray-600 mt-0.5">
                    Individual profile names are not returned in this screening response. Contact your
                    administrator if entity-level detail is required.
                  </span>
                </dd>
              </>
            )}
          </dl>

          {summary.needsReview && (
            <div className="mt-3 px-3 py-2 rounded-md bg-amber-100 border border-amber-200 text-sm text-amber-900">
              <strong>Review required</strong>
              {summary.overallStatus && (
                <span> — overall screening status: {formatThreatLevel(summary.overallStatus)}</span>
              )}
              .
            </div>
          )}

          {!compact && pepLists.length > 0 && (
            <details className="mt-3">
              <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                PEP list groups matched ({pepLists.length})
              </summary>
              <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-gray-800 space-y-1 list-disc pl-5">
                {pepLists.map((hit) => (
                  <li key={hit.id || hit.listName}>
                    {sanitizeVendorBranding(hit.listName)}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {narrative && !compact && (
            <p className="mt-3 text-xs text-gray-700 leading-relaxed border-t border-amber-200/80 pt-3">
              {narrative}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
